/**
 * @file PublishPageItem.tsx
 * @description Пункт страницы или места в окне «Опубликовать» (phase19.16
 * Р1-6а; Р3а — uiux-r3 §3.2, SB105, SB128): «[✓] Страница «О нас» · 3 правки
 * · вы ›», «[ ] «Акции» — скрыта. Показать на сайте». «›» раскрывает правки
 * по блокам; у скрытой страницы с правками — галочка «показывать на сайте»
 * (по умолчанию снята). Без права публикации пункт неактивен.
 *
 * Р3в (SB131, uiux-r3 §3.2): адрес на сайте сменится — строка «/about →
 * /about-us, старый будет переадресован [✓]» (галочка по умолчанию стоит;
 * снятая — старые ссылки перестанут работать).
 */

import { ChevronRight } from "lucide-react";
import { useState } from "react";

import { Button } from "../../ui/button";
import { CheckRow } from "../CheckRow";
import { PageDiffList } from "../../site-builder/publish/PageDiffList";
import type { PublishPageRow, RowSelection } from "../../../lib/site-builder/page-publish-items";
import { cn } from "@/lib/utils";

interface Props {
  row: PublishPageRow;
  value: RowSelection;
  disabled: boolean;
  onChange: (patch: Partial<RowSelection>) => void;
}

export function PublishPageItem({ row, value, disabled, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const canExpand = !row.showOnly && row.source.diff.items.length > 0;
  return (
    <div className="flex flex-col">
      <div className="flex min-w-0 items-center gap-1">
        <div className="min-w-0 flex-1">
          <CheckRow checked={value.checked} disabled={disabled} onChange={(checked) => onChange({ checked })}>
            <span className="min-w-0 break-words">
              {row.title}
              <span className="type-caption text-on-surface-variant"> · {row.meta}</span>
            </span>
          </CheckRow>
        </div>
        {canExpand ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-12 shrink-0"
            aria-expanded={open}
            aria-label={open ? `Скрыть правки: ${row.title}` : `Что изменено: ${row.title}`}
            onClick={() => setOpen((v) => !v)}
          >
            <ChevronRight aria-hidden className={cn("transition-transform", open && "rotate-90")} />
          </Button>
        ) : null}
      </div>
      {open ? <PageDiffList items={row.source.diff.items} limit={12} className="pb-2 pl-7" /> : null}
      {row.slugChange && value.checked ? (
        <div className="pl-7">
          <CheckRow checked={value.redirect !== false} disabled={disabled} onChange={(redirect) => onChange({ redirect })}>
            <span className="min-w-0 break-words">
              Адрес {row.slugChange.from} → {row.slugChange.to}, старый будет переадресован
            </span>
          </CheckRow>
          {value.redirect === false ? <p className="type-caption pl-7 text-warning">Старые ссылки на {row.slugChange.from} перестанут работать.</p> : null}
        </div>
      ) : null}
      {row.hidden && !row.showOnly && value.checked ? (
        <div className="pl-7">
          <CheckRow checked={value.show} disabled={disabled} onChange={(show) => onChange({ show })}>
            Показывать на сайте (сейчас скрыта)
          </CheckRow>
        </div>
      ) : null}
    </div>
  );
}
